import { currentLanguage, hasKey, localeOf, translate } from '../i18n'
import { appBridge } from './app/bridge'

// The unit index is clamped on both sides: a fraction (an average of 0.4) or a
// huge total would otherwise index outside the list and print "undefined".
function scaled(value: number, base: number, units: string[]): string {
  if (!Number.isFinite(value) || value <= 0) return `0 ${units[0]}`
  const i = Math.max(0, Math.min(Math.floor(Math.log(value) / Math.log(base)), units.length - 1))
  return `${(value / Math.pow(base, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

export const formatBytes = (bytes: number): string => scaled(bytes, 1024, ['B', 'KB', 'MB', 'GB', 'TB', 'PB'])

/** A rate in BITS per second */
export const formatBps = (bps: number): string => scaled(bps, 1000, ['bps', 'Kbps', 'Mbps', 'Gbps'])

/**
 * A rate in BYTES per second — what the API's …BytesPerSec fields hold — shown
 * the way network people read it, in bits. (These used to be printed as if they
 * were bits already, so every rate on the UniFi pages read 8× too low.)
 */
export const formatByteRate = (bytesPerSec: number): string => formatBps((bytesPerSec ?? 0) * 8)

// Both date helpers accept a missing or unparsable value: one absent field in an
// API answer must not take the whole page down (it did, on the webhooks page).
const toDate = (v: string | Date | null | undefined): Date | null => {
  const d = v instanceof Date ? v : v ? new Date(v) : null
  return d && !Number.isNaN(d.getTime()) ? d : null
}

// in the language in use (dates in its own format)
export function formatRelative(dateStr: string | Date | null | undefined): string {
  const date = toDate(dateStr)
  if (!date) return '—'
  const lang = currentLanguage()
  const diff = Date.now() - date.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return translate(lang, 'common.justNow')
  if (mins < 60) return translate(lang, 'common.minutesAgo', { count: mins })
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return translate(lang, 'common.hoursAgo', { count: hrs })
  const days = Math.floor(hrs / 24)
  return translate(lang, 'common.daysAgo', { count: days })
}

export function formatDate(dateStr: string | Date | null | undefined): string {
  const date = toDate(dateStr)
  if (!date) return '—'
  return date.toLocaleDateString(localeOf(currentLanguage()), { day: '2-digit', month: 'short', year: 'numeric' })
}

export function formatDateTime(dateStr: string | Date | null | undefined): string {
  const date = toDate(dateStr)
  if (!date) return '—'
  return date.toLocaleString(localeOf(currentLanguage()), { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function clsx(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ')
}

/** Inactivity limits offered in the dashboard (minutes): 5 minutes to 30 days */
export const IDLE_CHOICES = [5, 10, 15, 30, 60, 120, 240, 480, 720, 1440, 2880, 10080, 20160, 43200]
export function formatMinutes(m: number): string {
  const lang = currentLanguage()
  if (m < 60) return translate(lang, 'common.duration.minutes', { count: m })
  if (m < 1440) return translate(lang, 'common.duration.hours', { count: m / 60 })
  if (m % 10080 === 0) return translate(lang, 'common.duration.weeks', { count: m / 10080 })
  return translate(lang, 'common.duration.days', { count: Math.round(m / 1440) })
}

/** A device action (reboot, firmware_check, …) as people read it; an unknown one as written */
export function actionName(action: string): string {
  const key = `devices.action.${action}`
  return hasKey(key) ? translate(currentLanguage(), key) : action.replace(/_/g, ' ')
}

/** How long a device has been up: "3d 4h", "2h 15m", "40m" in the language in use */
export function formatUptime(seconds: number): string {
  const lang = currentLanguage()
  const d = Math.floor(seconds / 86400), h = Math.floor((seconds % 86400) / 3600), m = Math.floor((seconds % 3600) / 60)
  return d > 0 ? translate(lang, 'common.uptime.days', { d, h }) : h > 0 ? translate(lang, 'common.uptime.hours', { h, m }) : translate(lang, 'common.uptime.minutes', { m })
}

/** The licence module (device brand) a site's provider belongs to — mirrors PROVIDER_MODULE in the API. */
export function providerModule(provider?: string): 'unifi' | 'teltonika' | 'peplink' | 'mikrotik' | 'velanox' | 'omada' | 'ruckus' | 'rental' {
  // an event's own site is Rental's
  if (provider === 'EVENT') return 'rental'
  if (provider?.startsWith('VELANOX')) return 'velanox'
  if (provider?.startsWith('TELTONIKA')) return 'teltonika'
  if (provider?.startsWith('PEPLINK')) return 'peplink'
  if (provider?.startsWith('MIKROTIK')) return 'mikrotik'
  if (provider === 'OMADA') return 'omada'
  if (provider === 'RUCKUS_SZ') return 'ruckus'
  return 'unifi'
}

/** A site's own page: each brand has one (the generic page reads only what a UniFi controller syncs) */
export function sitePath(site: { id: string; provider?: string }): string {
  const p = site.provider
  if (p === 'UNIFI_HOST') return `/sites/${site.id}/unifi-host`
  if (p?.startsWith('VELANOX')) return `/sites/${site.id}/velanox`
  if (p?.startsWith('TELTONIKA')) return `/sites/${site.id}/teltonika`
  if (p?.startsWith('PEPLINK')) return `/sites/${site.id}/peplink`
  if (p?.startsWith('MIKROTIK')) return `/sites/${site.id}/mikrotik`
  // a site of a controller (Omada, SmartZone): one page for every such vendor
  if (p === 'OMADA' || p === 'RUCKUS_SZ') return `/sites/${site.id}/cloud`
  // a rental event's own site: what is loaded for it
  if (p === 'EVENT') return `/sites/${site.id}/event`
  return `/sites/${site.id}`
}

/** A code (a status, an alert category) as people read it in the language in use; a code without a text shows as it is */
export function codeText(prefix: 'common.status' | 'monitoring.category' | 'devices.unifihost.mgmt' | 'devices.unifihost.sec', code: string): string {
  const key = `${prefix}.${code}`
  return hasKey(key) ? translate(currentLanguage(), key) : code.replace(/_/g, ' ')
}
export const statusLabel = (status: string) => codeText('common.status', status)

/**
 * A provisioned MikroTik unit that is not out yet (on a shelf, quiet): its waiting or offline state,
 * which the list and its page show in grey; null for any other site or state
 */
export function notDeployedState(site: { status: string; provisionedAt?: string | null }): 'waiting' | 'offline' | null {
  if (!site.provisionedAt) return null
  return site.status === 'PENDING_VPN' ? 'waiting' : site.status === 'OFFLINE' ? 'offline' : null
}

export function statusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    ONLINE: 'badge-green',
    ACTIVE: 'badge-green',
    COMPLETED: 'badge-green',
    WARNING: 'badge-yellow',
    PENDING: 'badge-yellow',
    PENDING_VPN: 'badge-yellow',
    PROVISIONING: 'badge-yellow',
    RUNNING: 'badge-blue',
    OFFLINE: 'badge-red',
    ERROR: 'badge-red',
    FAILED: 'badge-red',
    EXPIRED: 'badge-red',
    SUSPENDED: 'badge-red',
    INACTIVE: 'badge-gray',
    DELETED: 'badge-gray',
  }
  return map[status] ?? 'badge-gray'
}

export function alertLevelClass(level: string): string {
  const map: Record<string, string> = {
    CRITICAL: 'alert-bar-critical',
    ERROR: 'alert-bar-error',
    WARNING: 'alert-bar-warning',
    INFO: 'alert-bar-info',
  }
  return map[level] ?? 'alert-bar-info'
}

/**
 * A VPN config's file name, as the server names it (backend/src/modules/vpn/ovpn-name.ts):
 * velanox-<company>-<person>.ovpn or velanox-<device>.ovpn — lowercase, letters and digits, hyphens
 */
export function ovpnFileName(parts: (string | null | undefined)[]): string {
  const words = parts
    .map(p => (p ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/ø/gi, 'o').replace(/æ/gi, 'ae').replace(/ß/g, 'ss').replace(/đ/gi, 'd').replace(/ł/gi, 'l'))
    .map(p => p.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))
    .filter(Boolean)
  return `velanox-${words.join('-').slice(0, 60).replace(/-+$/, '') || 'vpn'}.ovpn`
}

/** The file name a download's answer gives (Content-Disposition), else the fallback */
export function fileNameOf(res: { headers?: Record<string, unknown> }, fallback: string): string {
  return /filename="([^"]+)"/.exec(String(res.headers?.['content-disposition'] ?? ''))?.[1] ?? fallback
}

export function downloadBlob(blob: Blob, filename: string) {
  // in the app: the phone's share sheet (save to Files, send by mail)
  const app = appBridge()
  if (app?.features.files) { void app.share({ name: filename, mime: blob.type, data: blob }); return }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + '…' : str
}


/** Same rule as the API: refuse CSS that can load external resources */
const UNSAFE_CSS = /url\s*\(|@import|image-set|expression\s*\(|-moz-binding|behavior\s*:|\\/i

/** Inject company CSS unless it could exfiltrate input (older stored values) */
export function applyCustomCss(id: string, css: string | null | undefined): void {
  document.getElementById(id)?.remove()
  if (!css || UNSAFE_CSS.test(css)) return
  const style = document.createElement('style')
  style.id = id
  style.textContent = css
  document.head.appendChild(style)
}

/**
 * A company's chosen font, from Google Fonts. One <link>, replaced when the
 * choice changes and removed when there is none — so the font is there after a
 * page reload too, not only when the user came in through the login page.
 */
export function loadBrandFont(family: string | null | undefined): string {
  const name = (family ?? '').replace(/[^A-Za-z0-9 ]/g, '').trim()
  const el = document.getElementById('branding-font') as HTMLLinkElement | null
  if (!name) { el?.remove(); return '' }
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}:wght@300;400;500;600;700&display=swap`
  if (el?.getAttribute('href') !== href) {
    const link = el ?? document.head.appendChild(Object.assign(document.createElement('link'), { id: 'branding-font', rel: 'stylesheet' }))
    link.setAttribute('href', href)
  }
  return name
}
